import React, { useState, useEffect } from "react";
import { Clock, Calendar, BookOpen } from 'lucide-react';
import DatePicker from "react-datepicker";
import { format } from "date-fns";
import "react-datepicker/dist/react-datepicker.css";

function Timeline() {
  const [selectedDate, setSelectedDate] = useState(null);
  const [date, setDate] = useState("");
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [featuredDates] = useState([
    "15 August 1947",
    "4 July 1776",
    "11 September 2001",
    "14 April 1865",
    "20 July 1969"
  ]);
  const [groupedEvents, setGroupedEvents] = useState({});
  const [animationActive, setAnimationActive] = useState(false);

  useEffect(() => {
    if (events.length > 0) {
      const grouped = categorizeEvents(events);
      setGroupedEvents(grouped);
      setAnimationActive(true);
    } else {
      setGroupedEvents({});
    }
  }, [events]);

  const categorizeEvents = (eventsList) => {
    const categories = {
      "Political": [],
      "Cultural": [],
      "Scientific": [],
      "Other": []
    };

    eventsList.forEach(event => {
      const eventText = event.toLowerCase();
      if (eventText.match(/president|government|treaty|war|independence|election/)) {
        categories["Political"].push(event);
      } else if (eventText.match(/film|movie|music|artist|song|novel|book/)) {
        categories["Cultural"].push(event);
      } else if (eventText.match(/scientist|discovery|invention|technology/)) {
        categories["Scientific"].push(event);
      } else {
        categories["Other"].push(event);
      }
    });

    return Object.fromEntries(
      Object.entries(categories).filter(([_, events]) => events.length > 0)
    );
  };

  const handleDateChange = (date) => {
    if (date) {
      setSelectedDate(date);
      const formattedDate = format(date, "d MMMM yyyy");
      setDate(formattedDate);
      setTimeout(() => fetchEvents(), 100);
    }
  };

  const fetchEvents = async () => {
    if (!date.trim()) {
      setError("Please enter a valid date!");
      return;
    }

    setLoading(true);
    setError("");
    setAnimationActive(false);

    try {
      const res = await fetch(`https://aitutorbackend-production.up.railway.app/history/timeline?date=${encodeURIComponent(date)}`);
      if (!res.ok) throw new Error(`HTTP error! Status: ${res.status}`);
      
      const data = await res.json();
      if (data.events && data.events.length > 0) {
        setEvents(data.events);
      } else {
        setEvents([]);
        setError("No specific events found for this date.");
      }
    } catch (err) {
      console.error("Error fetching timeline events:", err);
      setError(`Error: ${err.message || "Failed to fetch events"}`);
    }

    setLoading(false);
  };

  const handleFeaturedDateClick = (featuredDate) => {
    const [day, month, year] = featuredDate.split(" ");
    const dateObj = new Date(`${month} ${day}, ${year}`);
    setSelectedDate(dateObj);
    setDate(featuredDate);
    setTimeout(() => fetchEvents(), 100);
  };

  // Custom styles for the date picker
  const customDatePickerStyles = {
    datePickerContainer: "relative",
    datePickerInput: `
      w-full pl-10 pr-4 py-3 border-2 border-[#8b4513] rounded-lg 
      bg-[#fff8e7] focus:ring-2 focus:ring-[#8b4513] focus:border-[#8b4513]
      font-serif placeholder-[#8b4513]/60
    `,
    datePickerCalendar: `
      !bg-[#fff8e7] !border-2 !border-[#8b4513] !rounded-lg !font-serif
      !shadow-lg [&_.react-datepicker__header]:!bg-[#8b4513]
      [&_.react-datepicker__header]:!text-white
      [&_.react-datepicker__day--selected]:!bg-[#8b4513]
      [&_.react-datepicker__day--keyboard-selected]:!bg-[#8b4513]/80
      [&_.react-datepicker__day:hover]:!bg-[#8b4513]/20
      [&_.react-datepicker__current-month]:!text-white
      [&_.react-datepicker__day-name]:!text-white
    `
  };

  return (
    <div className="min-h-screen bg-[#f4e4bc] pt-16 font-serif">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex justify-center mb-4">
            <Clock className="h-16 w-16 text-[#8b4513]" />
          </div>
          <h1 className="text-4xl font-extrabold text-[#8b4513] sm:text-5xl md:text-6xl" 
              style={{ textShadow: '2px 2px 4px rgba(139, 69, 19, 0.2)' }}>
            Historical Chronicles
          </h1>
          <p className="mt-3 max-w-md mx-auto text-base text-[#5c2e0a] sm:text-lg md:mt-5 md:text-xl md:max-w-3xl">
            Journey through time and explore the events that shaped our world
          </p>
        </div>

        {/* Search Section */}
        <div className="bg-[#fffbf0] rounded-xl shadow-lg p-6 mb-8 border-4 border-[#8b4513]"
             style={{ boxShadow: '8px 8px 0px rgba(139, 69, 19, 0.2)' }}>
          <div className="max-w-3xl mx-auto">
            <div className="flex flex-col md:flex-row gap-4">
              <div className={`relative flex-1 ${customDatePickerStyles.datePickerContainer}`}>
                <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-[#8b4513] z-10" />
                <DatePicker
                  selected={selectedDate}
                  onChange={handleDateChange}
                  dateFormat="d MMMM yyyy"
                  className={customDatePickerStyles.datePickerInput}
                  calendarClassName={customDatePickerStyles.datePickerCalendar}
                  placeholderText="Select or type a date (e.g., 15 August 1947)"
                  showYearDropdown
                  scrollableYearDropdown
                  yearDropdownItemNumber={100}
                  maxDate={new Date()}
                />
              </div>
              <button
                onClick={fetchEvents}
                className="px-6 py-3 bg-[#8b4513] text-white rounded-lg hover:bg-[#6d3610] transition duration-200 flex-shrink-0 flex items-center justify-center gap-2"
                style={{ boxShadow: '3px 3px 0px rgba(139, 69, 19, 0.2)' }}
              >
                <BookOpen className="h-5 w-5" />
                Explore History
              </button>
            </div>

            {/* Featured Dates */}
            <div className="mt-6">
              <h3 className="text-sm font-semibold text-[#8b4513] uppercase tracking-wider mb-3">
                Notable Dates in History
              </h3>
              <div className="flex flex-wrap gap-2">
                {featuredDates.map((featuredDate) => (
                  <button
                    key={featuredDate}
                    onClick={() => handleFeaturedDateClick(featuredDate)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 ${
                      date === featuredDate
                        ? 'bg-[#8b4513] text-white'
                        : 'bg-[#fff8e7] text-[#8b4513] hover:bg-[#ffe4b5] border-2 border-[#8b4513]'
                    }`}
                    style={{ boxShadow: '2px 2px 0px rgba(139, 69, 19, 0.2)' }}
                  >
                    {featuredDate}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#8b4513] border-t-transparent"></div>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-8 rounded-lg">
            <div className="flex">
              <div className="ml-3">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Timeline Results */}
        {Object.keys(groupedEvents).length > 0 && (
          <div className="relative">
            <div className="absolute left-1/2 transform -translate-x-1/2 h-full w-2 bg-[#8b4513]"></div>
            
            {Object.entries(groupedEvents).map(([category, categoryEvents], categoryIndex) => (
              <div 
                key={category}
                className={`relative mb-8 ${
                  animationActive ? 'animate-fadeIn' : ''
                }`}
                style={{
                  animationDelay: `${categoryIndex * 0.3}s`,
                  opacity: animationActive ? 1 : 0
                }}
              >
                <div className={`flex items-center ${
                  categoryIndex % 2 === 0 ? 'flex-row' : 'flex-row-reverse'
                }`}>
                  <div className={`w-1/2 ${
                    categoryIndex % 2 === 0 ? 'pr-8 text-right' : 'pl-8'
                  }`}>
                    <div className="bg-[#fffbf0] p-6 rounded-xl border-4 border-[#8b4513]"
                         style={{ boxShadow: '8px 8px 0px rgba(139, 69, 19, 0.2)' }}>
                      <h3 className="text-xl font-bold text-[#8b4513] mb-4">
                        {category} Events
                      </h3>
                      <ul className="space-y-4">
                        {categoryEvents.map((event, index) => (
                          <li 
                            key={index}
                            className={`text-[#5c2e0a] ${
                              animationActive ? 'animate-slideIn' : ''
                            }`}
                            style={{
                              animationDelay: `${(categoryIndex * 0.3) + (index * 0.15)}s`
                            }}
                          >
                            {event}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                  <div className="absolute left-1/2 transform -translate-x-1/2 w-8 h-8 bg-[#8b4513] rounded-full border-4 border-[#f4e4bc]"></div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default Timeline;